"use strict";
// Copyright (c) 2025 MDFriday. All rights reserved.
// This software is proprietary and confidential.
// Use of this source code is governed by the license that can be found in the LICENSE file.
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefineNode = exports.TemplateNode = exports.WithNode = exports.RangeNode = exports.IfNode = exports.BranchNode = exports.ContinueNode = exports.BreakNode = exports.ElseNode = exports.EndNode = exports.StringNode = exports.NumberNode = exports.BoolNode = exports.ChainNode = exports.FieldNode = exports.NilNode = exports.DotNode = exports.IdentifierNode = exports.VariableNode = exports.CommentNode = exports.CommandNode = exports.PipeNode = exports.ActionNode = exports.ListNode = exports.TemplateError = exports.TextNode = exports.StringBuilderImpl = exports.NodeType = void 0;
exports.setTextFormat = setTextFormat;
exports.getTextFormat = getTextFormat;
exports.errorf = errorf;
exports.sprintf = sprintf;
// Package template implements data-driven templates for generating textual output.
// This is the TypeScript implementation of the Go template package.
const parse_1 = require("./parse");
// textFormat is used for formatting text nodes. Changed to "%q" in tests for better error messages.
let textFormat = "%s";
// 提供函数来设置和获取 textFormat 变量
function setTextFormat(format) {
    const oldFormat = textFormat;
    textFormat = format;
    return oldFormat;
}
function getTextFormat() {
    return textFormat;
}
var NodeType;
(function (NodeType) {
    NodeType[NodeType["NodeText"] = 0] = "NodeText";
    NodeType[NodeType["NodeAction"] = 1] = "NodeAction";
    NodeType[NodeType["NodeBool"] = 2] = "NodeBool";
    NodeType[NodeType["NodeChain"] = 3] = "NodeChain";
    NodeType[NodeType["NodeCommand"] = 4] = "NodeCommand";
    NodeType[NodeType["NodeDot"] = 5] = "NodeDot";
    NodeType[NodeType["NodeElse"] = 6] = "NodeElse";
    NodeType[NodeType["NodeEnd"] = 7] = "NodeEnd";
    NodeType[NodeType["NodeField"] = 8] = "NodeField";
    NodeType[NodeType["NodeIdentifier"] = 9] = "NodeIdentifier";
    NodeType[NodeType["NodeIf"] = 10] = "NodeIf";
    NodeType[NodeType["NodeList"] = 11] = "NodeList";
    NodeType[NodeType["NodeNil"] = 12] = "NodeNil";
    NodeType[NodeType["NodeNumber"] = 13] = "NodeNumber";
    NodeType[NodeType["NodePipe"] = 14] = "NodePipe";
    NodeType[NodeType["NodeRange"] = 15] = "NodeRange";
    NodeType[NodeType["NodeString"] = 16] = "NodeString";
    NodeType[NodeType["NodeTemplate"] = 17] = "NodeTemplate";
    NodeType[NodeType["NodeVariable"] = 18] = "NodeVariable";
    NodeType[NodeType["NodeWith"] = 19] = "NodeWith";
    NodeType[NodeType["NodeComment"] = 20] = "NodeComment";
    NodeType[NodeType["NodeBreak"] = 21] = "NodeBreak";
    NodeType[NodeType["NodeContinue"] = 22] = "NodeContinue"; // A continue action.
})(NodeType || (exports.NodeType = NodeType = {}));
// Concrete implementation of StringBuilder
class StringBuilderImpl {
    constructor() {
        this.parts = [];
    }
    WriteString(str) {
        this.parts.push(str);
    }
    String() {
        return this.parts.join("");
    }
}
exports.StringBuilderImpl = StringBuilderImpl;
// TextNode holds plain text.
class TextNode {
    constructor(tr, pos, text) {
        this.nodeType = NodeType.NodeText;
        this.tr = tr;
        this.pos = pos;
        // Convert string to Uint8Array to match Go's []byte
        this.Text = new TextEncoder().encode(text);
    }
    Type() {
        return this.nodeType;
    }
    String() {
        // 使用 textFormat 格式化文本，与 Go 版本保持一致
        return sprintf(textFormat, new TextDecoder().decode(this.Text));
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    Copy() {
        return new TextNode(this.tr, this.pos, new TextDecoder().decode(this.Text));
    }
    writeTo(sb) {
        const text = new TextDecoder().decode(this.Text);
        sb.WriteString(sprintf(textFormat, text));
    }
}
exports.TextNode = TextNode;
// Error handling utilities
class TemplateError extends Error {
    constructor(message) {
        super(message);
        this.name = "TemplateError";
    }
}
exports.TemplateError = TemplateError;
function errorf(format, ...args) {
    throw new TemplateError(sprintf(format, ...args));
}
// Update sprintf to handle more format specifiers
function sprintf(format, ...args) {
    let argIndex = 0;
    return format.replace(/%[sqd%vT]/g, (match) => {
        if (match === '%%')
            return '%';
        if (argIndex >= args.length)
            return match;
        const arg = args[argIndex++];
        switch (match) {
            case '%s':
                return String(arg);
            case '%q':
                // 对于 %q 格式，我们需要将字符串用双引号括起来，与 Go 版本的行为一致
                if (typeof arg === 'string') {
                    return `"${arg}"`;
                }
                else {
                    return String(arg);
                }
            case '%d':
                return Number(arg).toString();
            case '%v':
                return arg instanceof Error ? arg.message : String(arg);
            case '%T':
                return typeof arg;
            default:
                return match;
        }
    });
}
class ListNode {
    constructor(pos) {
        this.Nodes = []; // Initialize empty array
        this.pos = pos;
    }
    Type() {
        return NodeType.NodeList;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    append(n) {
        this.Nodes.push(n);
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    CopyList() {
        const n = new ListNode(this.pos);
        n.tr = this.tr;
        n.Nodes = this.Nodes.map(node => node.Copy());
        return n;
    }
    Copy() {
        return this.CopyList();
    }
    writeTo(sb) {
        this.Nodes.forEach(node => node.writeTo(sb));
    }
}
exports.ListNode = ListNode;
class ActionNode {
    constructor(pos, line, pipe) {
        this.pos = pos;
        this.line = line;
        this.Pipe = pipe;
    }
    Type() {
        return NodeType.NodeAction;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    setPos(pos) {
        this.pos = pos;
        return this;
    }
    String() {
        return `{{${this.Pipe.String()}}}`;
    }
    Copy() {
        return new ActionNode(this.pos, this.line, this.Pipe.Copy());
    }
    writeTo(sb) {
        sb.WriteString("{{");
        this.Pipe.writeTo(sb);
        sb.WriteString("}}");
    }
}
exports.ActionNode = ActionNode;
class PipeNode {
    constructor(pos, line) {
        this.IsAssign = false;
        this.Decl = [];
        this.Cmds = [];
        this.pos = pos;
        this.Line = line;
    }
    Type() {
        return NodeType.NodePipe;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    append(command) {
        this.Cmds.push(command);
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        const n = new PipeNode(this.pos, this.Line);
        n.IsAssign = this.IsAssign;
        n.Decl = this.Decl.map(v => v.Copy());
        n.Cmds = this.Cmds.map(c => c.Copy());
        return n;
    }
    writeTo(sb) {
        if (this.Decl.length > 0) {
            this.Decl.forEach((v, i) => {
                if (i > 0) {
                    sb.WriteString(", ");
                }
                v.writeTo(sb);
            });
            sb.WriteString(" := ");
        }
        this.Cmds.forEach((c, i) => {
            if (i > 0) {
                sb.WriteString(" | ");
            }
            c.writeTo(sb);
        });
    }
}
exports.PipeNode = PipeNode;
class CommandNode {
    constructor(pos) {
        this.Args = []; // Initialize empty array
        this.pos = pos;
    }
    Type() {
        return NodeType.NodeCommand;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    append(arg) {
        this.Args.push(arg);
    }
    Copy() {
        const n = new CommandNode(this.pos);
        n.Args = this.Args.map(a => a.Copy());
        return n;
    }
    writeTo(sb) {
        this.Args.forEach((a, i) => {
            if (i > 0) {
                sb.WriteString(" ");
            }
            if (a.Type() === NodeType.NodePipe) {
                sb.WriteString("(");
                a.writeTo(sb);
                sb.WriteString(")");
            }
            else {
                a.writeTo(sb);
            }
        });
    }
}
exports.CommandNode = CommandNode;
class CommentNode {
    constructor(pos, text) {
        this.pos = pos;
        this.Text = text;
    }
    Type() {
        return NodeType.NodeComment;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        return new CommentNode(this.pos, this.Text);
    }
    writeTo(sb) {
        // 只有在 ParseComments 模式下才输出注释内容
        if (this.tr && (this.tr.Mode & parse_1.Mode.ParseComments)) {
            sb.WriteString("{{");
            sb.WriteString(this.Text);
            sb.WriteString("}}");
        }
    }
}
exports.CommentNode = CommentNode;
class VariableNode {
    constructor(pos, ident) {
        this.pos = pos;
        this.Ident = ident;
    }
    Type() {
        return NodeType.NodeVariable;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        const result = sb.String();
        return result;
    }
    Copy() {
        return new VariableNode(this.pos, this.Ident.slice());
    }
    writeTo(sb) {
        for (let i = 0; i < this.Ident.length; i++) {
            if (i > 0) {
                sb.WriteString(".");
            }
            sb.WriteString(this.Ident[i]);
        }
    }
}
exports.VariableNode = VariableNode;
class IdentifierNode {
    constructor(ident) {
        this.Ident = ident;
    }
    Type() {
        return NodeType.NodeIdentifier;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    setPos(pos) {
        this.pos = pos;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        return new IdentifierNode(this.Ident).setTree(this.tr).setPos(this.pos);
    }
    writeTo(sb) {
        sb.WriteString(this.Ident);
    }
}
exports.IdentifierNode = IdentifierNode;
class DotNode {
    constructor(pos) {
        this.pos = pos;
    }
    Type() {
        return NodeType.NodeDot;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        return new DotNode(this.pos);
    }
    writeTo(sb) {
        sb.WriteString(".");
    }
}
exports.DotNode = DotNode;
class NilNode {
    constructor(pos) {
        this.pos = pos;
    }
    Type() {
        return NodeType.NodeNil;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    setPos(pos) {
        this.pos = pos;
        return this;
    }
    String() {
        return "nil";
    }
    Copy() {
        return new NilNode(this.pos);
    }
    writeTo(sb) {
        sb.WriteString("nil");
    }
}
exports.NilNode = NilNode;
class FieldNode {
    constructor(pos, ident) {
        this.pos = pos;
        this.Ident = ident;
    }
    Type() {
        return NodeType.NodeField;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    setPos(pos) {
        this.pos = pos;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        return new FieldNode(this.pos, this.Ident.slice());
    }
    writeTo(sb) {
        for (const id of this.Ident) {
            sb.WriteString(".");
            sb.WriteString(id);
        }
    }
}
exports.FieldNode = FieldNode;
class ChainNode {
    constructor(pos, node) {
        this.Field = [];
        this.pos = pos;
        this.Node = node;
    }
    Type() {
        return NodeType.NodeChain;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    setPos(pos) {
        this.pos = pos;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        const result = sb.String();
        return result;
    }
    add(field) {
        if (field.length === 0 || field[0] !== '.') {
            console.error(`[ChainNode.add] Field does not start with a dot: "${field}"`);
            throw new Error("no dot in field");
        }
        const cleanField = field.substring(1);
        if (cleanField === "") {
            console.error(`[ChainNode.add] Empty field after removing dot`);
            throw new Error("empty field");
        }
        this.Field.push(cleanField);
    }
    Copy() {
        const n = new ChainNode(this.pos, this.Node.Copy());
        n.Field = this.Field.slice();
        return n;
    }
    writeTo(sb) {
        if (this.Node.Type() === NodeType.NodePipe) {
            sb.WriteString("(");
            this.Node.writeTo(sb);
            sb.WriteString(")");
        }
        else {
            this.Node.writeTo(sb);
        }
        this.Field.forEach(f => {
            sb.WriteString(".");
            sb.WriteString(f);
        });
    }
}
exports.ChainNode = ChainNode;
class BoolNode {
    constructor(pos, value) {
        this.pos = pos;
        this.True = value;
    }
    Type() {
        return NodeType.NodeBool;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        return new BoolNode(this.pos, this.True);
    }
    writeTo(sb) {
        sb.WriteString(this.True ? "true" : "false");
    }
}
exports.BoolNode = BoolNode;
class NumberNode {
    constructor(tr, pos, text) {
        this.IsInt = false; // Number has an integral value.
        this.IsUint = false; // Number has an unsigned integral value.
        this.IsFloat = false; // Number has a floating-point value.
        this.IsComplex = false; // Number is complex.
        this.Int64 = 0; // The signed integer value.
        this.Uint64 = 0; // The unsigned integer value.
        this.Float64 = 0; // The floating-point value.
        this.Complex128 = { re: 0, im: 0 }; // The complex value.
        this.tr = tr;
        this.pos = pos;
        this.Text = text;
    }
    Type() {
        return NodeType.NodeNumber;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    String() {
        return this.Text;
    }
    Copy() {
        const n = new NumberNode(this.tr, this.pos, this.Text);
        n.IsInt = this.IsInt;
        n.IsUint = this.IsUint;
        n.IsFloat = this.IsFloat;
        n.IsComplex = this.IsComplex;
        n.Int64 = this.Int64;
        n.Uint64 = this.Uint64;
        n.Float64 = this.Float64;
        n.Complex128 = Object.assign({}, this.Complex128);
        return n;
    }
    writeTo(sb) {
        sb.WriteString(this.Text);
    }
    // Helper method to simplify complex numbers
    simplifyComplex() {
        if (this.Complex128.im === 0) {
            this.IsFloat = true;
            this.Float64 = this.Complex128.re;
            if (Number.isInteger(this.Float64)) {
                this.IsInt = true;
                this.Int64 = this.Float64;
                if (this.Float64 >= 0) {
                    this.IsUint = true;
                    this.Uint64 = this.Float64;
                }
            }
        }
    }
}
exports.NumberNode = NumberNode;
class StringNode {
    constructor(pos, quoted, text) {
        this.pos = pos;
        this.Quoted = quoted;
        this.Text = text;
    }
    Type() {
        return NodeType.NodeString;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        return new StringNode(this.pos, this.Quoted, this.Text);
    }
    writeTo(sb) {
        sb.WriteString(this.Quoted);
    }
}
exports.StringNode = StringNode;
class EndNode {
    constructor(pos) {
        this.pos = pos;
    }
    Type() {
        return NodeType.NodeEnd;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        return new EndNode(this.pos);
    }
    writeTo(sb) {
        sb.WriteString("{{end}}");
    }
}
exports.EndNode = EndNode;
class ElseNode {
    constructor(pos, line) {
        this.pos = pos;
        this.Line = line;
    }
    Type() {
        return NodeType.NodeElse;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        return new ElseNode(this.pos, this.Line);
    }
    writeTo(sb) {
        sb.WriteString("{{else}}");
    }
}
exports.ElseNode = ElseNode;
class BreakNode {
    constructor(pos, line) {
        this.pos = pos;
        this.Line = line;
    }
    Type() {
        return NodeType.NodeBreak;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        return new BreakNode(this.pos, this.Line);
    }
    writeTo(sb) {
        sb.WriteString("{{break}}");
    }
}
exports.BreakNode = BreakNode;
class ContinueNode {
    constructor(pos, line) {
        this.pos = pos;
        this.Line = line;
    }
    Type() {
        return NodeType.NodeContinue;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        return new ContinueNode(this.pos, this.Line);
    }
    writeTo(sb) {
        sb.WriteString("{{continue}}");
    }
}
exports.ContinueNode = ContinueNode;
// BranchNode is the common representation of if, range, and with.
class BranchNode {
    constructor(nodeType, tr, pos, line, pipe, list, elseList) {
        this.nodeType = nodeType;
        this.tr = tr;
        this.pos = pos;
        this.Line = line;
        this.Pipe = pipe;
        this.List = list;
        this.ElseList = elseList;
    }
    Type() {
        return this.nodeType;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    String() {
        const sb = new StringBuilderImpl();
        this.writeTo(sb);
        return sb.String();
    }
    Copy() {
        throw new Error("Copy() must be implemented by derived classes");
    }
    writeTo(sb) {
        const name = this.nodeType === NodeType.NodeIf ? "if" :
            this.nodeType === NodeType.NodeRange ? "range" :
                this.nodeType === NodeType.NodeWith ? "with" :
                    "unknown";
        sb.WriteString("{{" + name + " ");
        this.Pipe.writeTo(sb);
        sb.WriteString("}}");
        this.List.writeTo(sb);
        if (this.ElseList) {
            sb.WriteString("{{else}}");
            this.ElseList.writeTo(sb);
        }
        sb.WriteString("{{end}}");
    }
}
exports.BranchNode = BranchNode;
class IfNode extends BranchNode {
    constructor(tr, pos, line, pipe, list, elseList) {
        super(NodeType.NodeIf, tr, pos, line, pipe, list, elseList);
    }
    Copy() {
        var _a;
        return new IfNode(this.tr, this.pos, this.Line, this.Pipe.Copy(), this.List.CopyList(), ((_a = this.ElseList) === null || _a === void 0 ? void 0 : _a.CopyList()) || null);
    }
}
exports.IfNode = IfNode;
class RangeNode extends BranchNode {
    constructor(tr, pos, line, pipe, list, elseList) {
        super(NodeType.NodeRange, tr, pos, line, pipe, list, elseList);
    }
    Copy() {
        var _a;
        return new RangeNode(this.tr, this.pos, this.Line, this.Pipe.Copy(), this.List.CopyList(), ((_a = this.ElseList) === null || _a === void 0 ? void 0 : _a.CopyList()) || null);
    }
}
exports.RangeNode = RangeNode;
class WithNode extends BranchNode {
    constructor(tr, pos, line, pipe, list, elseList) {
        super(NodeType.NodeWith, tr, pos, line, pipe, list, elseList);
    }
    Copy() {
        var _a;
        return new WithNode(this.tr, this.pos, this.Line, this.Pipe.Copy(), this.List.CopyList(), ((_a = this.ElseList) === null || _a === void 0 ? void 0 : _a.CopyList()) || null);
    }
}
exports.WithNode = WithNode;
class TemplateNode {
    constructor(pos, line, name, pipe) {
        this.pos = pos;
        this.Line = line;
        this.Name = name;
        this.Pipe = pipe;
    }
    Type() {
        return NodeType.NodeTemplate;
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        return "{{template " + JSON.stringify(this.Name) + (this.Pipe ? " " + this.Pipe.String() : "") + "}}";
    }
    Copy() {
        return new TemplateNode(this.pos, this.Line, this.Name, this.Pipe ? this.Pipe.Copy() : null).setTree(this.tr);
    }
    writeTo(sb) {
        sb.WriteString("{{template ");
        sb.WriteString(JSON.stringify(this.Name));
        if (this.Pipe) {
            sb.WriteString(" ");
            this.Pipe.writeTo(sb);
        }
        sb.WriteString("}}");
    }
}
exports.TemplateNode = TemplateNode;
class DefineNode {
    constructor(pos, line, name, pipe) {
        this.pos = pos;
        this.Line = line;
        this.Name = name;
        this.Pipe = pipe;
    }
    Type() {
        return NodeType.NodeTemplate; // Reuse NodeTemplate type as in Go
    }
    Position() {
        return this.pos;
    }
    tree() {
        return this.tr;
    }
    setTree(t) {
        this.tr = t;
        return this;
    }
    String() {
        return "{{define " + this.Name + "}}";
    }
    Copy() {
        return new DefineNode(this.pos, this.Line, this.Name, this.Pipe.Copy()).setTree(this.tr);
    }
    writeTo(sb) {
        sb.WriteString("{{define " + this.Name + "}}");
        if (this.Pipe) {
            sb.WriteString(" ");
            this.Pipe.writeTo(sb);
        }
        sb.WriteString("}}");
    }
}
exports.DefineNode = DefineNode;
//# sourceMappingURL=node.js.map